import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ShareType } from '../../../../types/documents/share-types';
import { AlertService } from '../../../../services/alert.service';
import { DocumentsService } from '../../../../services/documents.service';

interface SharingOpts {
    public: boolean;
    documentsPermissions: { permission: number | string }[];
    userGroups: { bdId: number | string, name: string };
}

@Component({
    selector: 'sharing-form-fields',
    templateUrl: './sharing-form-fields.component.html',
    styleUrls: ['./sharing-form-fields.component.scss']
})
export class SharingFormFieldsComponent implements OnInit {

    @Input() isEditMode = false;
    @Output() isEditModeChange = new EventEmitter();

    private sharingOptsCache = null;

    private shareTypeValue: ShareType = null;

    public permissionsList = [];
    
    public shareTypes = ShareType;
    public shareTypeNames = Object.keys(ShareType).reduce((obj, key) => {
        obj[ShareType[key]] = key;
        return obj;
    }, {});

    public shareTypeOptions = Object.keys(ShareType).map(key => ({ name: key, value: ShareType[key] }));
    currentShareTypeOptions: Array<any> = this.shareTypeOptions;
    currentUserGroups: Array<any> = [];

    public multiselectSettings = {
        singleSelection: false,
        text: 'Select',
        selectAllText: 'Select All',
        unSelectAllText: 'Deselect All',
        enableSearchFilter: true
    };

    @Input() isCanEdit = false;

    private selectedItemValue;
    @Input() set selectedItem(value) {
        if (this.selectedItemValue === value) {
            return;
        }
        this.selectedItemValue = value;
        this.initFormValues();
    }

    get selectedItem() {
        return this.selectedItemValue;
    }

    @Output() selectedItemChange = new EventEmitter();

    @Output() sharingOptionsChange: EventEmitter<SharingOpts> = new EventEmitter();

    constructor(
        public ds: DocumentsService,
        private alertService: AlertService
    ) { }

    ngOnInit() {
        this.ds.getParentPermissions().subscribe(({ permissionsList, parentPermission, documentsPermissions, userGroups, isPublic }) => {
            this.permissionsList = permissionsList.map(this.ds.parsePermission).map(item => ({ ...item, permission: item.id }));
            
            if (documentsPermissions !== null || userGroups !== null || isPublic !== null) {
                let shareTypeValue = null;
                if (isPublic) {
                    shareTypeValue = ShareType.Public;
                } else if (documentsPermissions.length) {
                    shareTypeValue = ShareType.Residents;
                } else if (userGroups.length) {
                    shareTypeValue = ShareType.Groups;
                }
                
                this.currentUserGroups = JSON.parse(JSON.stringify(userGroups.map(role => ({
                        id: role.bdId,
                        itemName: role.name
                    }))
                ));

                this.currentShareTypeOptions = this.shareTypeOptions.filter(item => item.value == shareTypeValue)
            } else { 
                this.currentUserGroups = JSON.parse(JSON.stringify(this.ds.userGroups));        
            }
        });

    }

    initFormValues() {
        if (this.selectedItem.isPublic) {
            this.shareTypeValue = ShareType.Public;
        } else if (this.selectedItem.documentsPermissions.length) {
            this.shareTypeValue = ShareType.Residents;
        } else if (this.selectedItem.userGroups.length) {
            this.shareTypeValue = ShareType.Groups;
        } else {
            this.shareTypeValue = null;
        }
    }

    set shareType(value: ShareType) {
        this.shareTypeValue = value;

        if (this.shareType !== ShareType.Residents) {
            this.selectedItem.documentsPermissions = []; // force reset permissions
        } else if (!this.selectedItem.documentsPermissions.length && this.sharingOptsCache) {
            this.selectedItem.documentsPermissions = [...this.sharingOptsCache.documentsPermissions]; // set parent permissions as initial state
        }

        if (this.shareType !== ShareType.Groups) {
            this.selectedItem.userGroups = []; // force reset group sharing
        }

        this.selectedItem.isPublic = this.shareType === ShareType.Public;
    }

    get shareType(): ShareType {
        return this.shareTypeValue;
    }

    get sharingOptions(): SharingOpts {
        return {
            public: this.shareType === ShareType.Public,
            documentsPermissions: this.ds.selectedItemData.documentsPermissions.map(item => ({ permission: item.id })),
            userGroups: this.ds.selectedItemData.userGroups.map(item => ({
                bdId: item.id,
                name: item.itemName
            }))
        };
    }

    setEditMode(isEnabled = true) {
        this.isEditMode = isEnabled;
        this.isEditModeChange.next(isEnabled);
        if (isEnabled) {
            this.cacheSharingOpts();
        } else {
            this.restoreSharingOps();
        }
    }

    updateSharingOptions() {
        let isInvalid = false;

        if (this.shareType === ShareType.Groups && !(this.ds.selectedItemData.userGroups && this.ds.selectedItemData.userGroups.length)) {
            this.alertService.toast.warning('Group should not be blank.');
            isInvalid = true;
        }

        if (this.shareType === ShareType.Residents && !(this.ds.selectedItemData.documentsPermissions && this.ds.selectedItemData.documentsPermissions.length)) {
            this.alertService.toast.warning('Occupancy type should not be blank.');
            isInvalid = true;
        }

        if (isInvalid) {
            return;
        }

        this.sharingOptionsChange.next(this.sharingOptions);
    }

    onDocumentPermissionsChange(value) {
        this.selectedItem.documentsPermissions = value;
    }

    private cacheSharingOpts() {
        const { isPublic, documentsPermissions, userGroups } = this.selectedItem;
        this.sharingOptsCache = JSON.parse(JSON.stringify({ isPublic, documentsPermissions, userGroups }));
    }

    private restoreSharingOps() {
        Object.keys(this.sharingOptsCache).forEach(key => this.selectedItem[key] = this.sharingOptsCache[key]);
        this.initFormValues();
    }
}



// WEBPACK FOOTER //
// /app/src/app/views/documents/documents-detailed-view/sharing-form-fields/sharing-form-fields.component.ts